import { getUrl, StorageFile, useAttachment, usePlatformGet } from '@reapitform/hooks'
import { PhoneNumberCountryCode } from '@reapitform/utils'
import React, { useMemo } from 'react'

export interface IAgentSettingsData {
  referenceID: string
  details: {
    businessName: string
    tradingName: string
    abn: string
    acn: string
    logo?: { fileSize?: number } & Partial<StorageFile>
  }
  contactDetails: {
    phone: string
    mobile: string
    facsimile: string
    email: string
    emailCc: string // **multiple emails with comma separated pattern
    propertyAddressDisplay: string
    propertyAddress?: {
      unitNo: string
      streetNo: string
      street: string
      streetType: string
      suburb: string
      state: string
      postCode: string
      country: string
      twoLetterISORegionName: PhoneNumberCountryCode
    }
  }
  paymentMethods: {
    [key: string]: any
    directDebit?: {
      type: string
    }
    directCredit?: {
      accountName: string
      bsb: string
      bankName: string
      accountNumber: string
    }
    bpay?: {
      billerCode: string
    }
    cheque?: {
      payableTo: string
    }
    cash?: {
      placeOfPayment: string
    }
    other?: {
      type: string // limited to only 24 characters
    }
  }
}

export interface IAgentBusinessDetailsResponse extends Omit<IAgentSettingsData['details'], 'logo'> {
  logoFileSize?: number
  logoFileID?: string
}

export const useAgentBusinessDetails = () => {
  const { getFileInformation, isLoading: loadImageData } = useAttachment()
  const [businessDetailsData, setBusinessDetailsData] = React.useState<IAgentSettingsData['details'] | null>(null)
  const path: string = '/api/Organization/BusinessDetails'
  const [result, loading, errorString, , isRefetching] = usePlatformGet<IAgentBusinessDetailsResponse>({
    path,
    retry: false,
  })

  const queryKey = useMemo(getUrl(path), [path])

  React.useEffect(() => {
    if (!result) return

    const { logoFileID, logoFileSize, ...businessDetailsData } = result
    if (!logoFileID) return setBusinessDetailsData(businessDetailsData)

    getFileInformation(logoFileID)
      .then((data) => {
        const isDataObject: boolean = typeof data === 'object'

        setBusinessDetailsData({
          ...businessDetailsData,
          logo: isDataObject ? { ...data, fileSize: logoFileSize } : undefined,
        })
      })
      .catch(() => setBusinessDetailsData(businessDetailsData))
  }, [result])

  return {
    data: loading || loadImageData ? null : businessDetailsData,
    loading: Boolean(loading || loadImageData),
    isRefetching,
    errorString,
    queryKey,
  }
}

export const useAgentContactDetails = () => {
  const path: string = '/api/Organization/ContactDetails'
  const [result, loading, errorString, , isRefetching] = usePlatformGet<IAgentSettingsData['contactDetails']>({
    path,
    retry: false,
  })

  const queryKey = useMemo(getUrl(path), [path])

  return { data: loading ? null : result, loading, errorString, queryKey, isRefetching }
}

export const useAgentPaymentMethods = () => {
  const path: string = '/api/Organization/PaymentMethods'
  const [result, loading, errorString, , isRefetching] = usePlatformGet<IAgentSettingsData['paymentMethods']>({
    path,
    retry: false,
  })

  const queryKey = useMemo(getUrl(path), [path])

  return { data: loading ? null : result, loading, errorString, queryKey, isRefetching }
}
